import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import LanguageSelector from "./LanguageSelector";
import CurrencySelector from "./CurrencySelector";
import { Button } from "../components/ui/button";
import { ShoppingBag, User, Menu, Instagram, Facebook, Youtube, LogOut, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { CURRENT_BRAND } from "../config/brand";

export default function Navbar() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const doSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term) navigate(`/cerca?q=${encodeURIComponent(term)}`);
    else navigate("/cerca");
  };

  const items = [
    { to: "/", label: t("nav.home", "Home") },
    { to: "/vini", label: t("nav.wines", "Scelta dei Vini") },
    { to: "/box", label: t("nav.boxes", "Scelta Food Box") },
    { to: "/esperienze", label: t("nav.experiences", "Esperienze") },
    { to: "/tv", label: t("nav.tv", "Amazonico TV") },
    { to: "/club", label: t("nav.club", "Club Privilegia") },
    { to: "/ai-ricette", label: t("nav.airecipes", "AI Ricette") },
    { to: "/rivista", label: t("nav.magazine", "Rivista") },
    { to: "/punti", label: t("nav.rewards", "Punti Premio") },
    { to: "/abbonamenti", label: t("nav.subscriptions", "Abbonamenti") },
    { to: "/franchising", label: t("nav.franchising", "Franchising") },
    { to: "/negozi", label: t("nav.stores", "Le nostre aziende") },
    { to: "/azienda", label: t("nav.sell", "Vendi con noi") },
  ];

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 backdrop-blur-md bg-[hsl(var(--background))]/92 border-b border-border"
    >
      {/* TOP UTILITY STRIP */}
      <div className="border-b border-border/60 bg-[hsl(var(--muted))]/50">
        <div className="container-xl h-9 flex items-center justify-between text-[11px] uppercase tracking-[0.24em]">
          <div className="hidden md:flex items-center gap-4 text-muted-foreground">
            <span>{t("utility.shipping_free", "Spedizione gratuita in IT da €80")}</span>
            <span className="h-3 w-px bg-border" />
            <span>{t("utility.fast_delivery", "Consegna refrigerata 24–48h")}</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            {/* Social icons */}
            <div className="hidden sm:flex items-center gap-1.5 mr-2" data-testid="nav-social">
              <a href="https://www.instagram.com/amazonico3000/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="nav-social-instagram" className="p-1.5 text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61574281947800" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="nav-social-facebook" className="p-1.5 text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://www.youtube.com/@AMAZONICO3000" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="nav-social-youtube" className="p-1.5 text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
            <span className="hidden sm:inline-block h-3 w-px bg-border mr-1" />
            <Link
              to="/b2b"
              data-testid="nav-b2b"
              className="hidden sm:inline-flex px-3 py-1.5 font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] transition-colors"
            >
              {t("nav.b2b", "B2B Affiliate")}
            </Link>
            <span className="hidden sm:inline-block h-3 w-px bg-border" />
            <LanguageSelector />
            <span className="hidden md:inline-block h-3 w-px bg-border" />
            <div className="hidden md:block"><CurrencySelector /></div>
            <span className="hidden sm:inline-block h-3 w-px bg-border" />
            {user ? (
              <>
                {user.role === "super_admin" && (
                  <Link to="/admin" data-testid="nav-admin" className="hidden md:inline-flex px-3 py-1.5 text-[hsl(var(--primary))] font-semibold hover:text-[hsl(var(--accent))]">Admin</Link>
                )}
                {user.is_vendor && (
                  <Link to="/azienda" data-testid="nav-azienda" className="hidden md:inline-flex px-3 py-1.5 text-[hsl(var(--primary))] font-semibold hover:text-[hsl(var(--accent))]">Backend Azienda</Link>
                )}
                <Link to={user.is_vendor ? "/azienda" : "/area-utente"} data-testid="nav-account" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-foreground/80 hover:text-[hsl(var(--primary))]">
                  <User className="h-3.5 w-3.5" /> {user.name?.split(" ")[0] || "Account"}
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-foreground/80 hover:bg-destructive hover:text-white hover:border-destructive transition-colors font-semibold"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  {t("nav.logout", "Esci")}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="px-3 py-1.5 text-foreground/80 hover:text-[hsl(var(--primary))] font-medium">{t("nav.login", "Accedi")}</Link>
                <Link to="/register" data-testid="nav-register" className="hidden md:inline-flex px-3 py-1.5 text-[hsl(var(--primary))] font-semibold hover:text-[hsl(var(--accent))]">{t("nav.register", "Registrati")}</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* BRAND BAND */}
      <div className="container-xl h-24 md:h-28 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img src={CURRENT_BRAND.logoNav} alt={CURRENT_BRAND.name} className="h-16 md:h-24 w-auto transition-transform duration-500 group-hover:scale-[1.03]" />
        </Link>

        <div className="flex items-center gap-2">
          <form onSubmit={doSearch} className="hidden md:flex items-center relative mr-1" data-testid="navbar-search">
            <Search className="h-4 w-4 absolute left-3 text-muted-foreground pointer-events-none" />
            <input
              data-testid="navbar-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("nav.search_ph", "Cerca prodotti…")}
              className="w-40 lg:w-56 pl-9 pr-3 py-2 text-sm border border-border rounded-full bg-[hsl(var(--muted))]/40 focus:outline-none focus:border-[hsl(var(--primary))] focus:bg-card transition-all"
            />
          </form>
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center justify-center h-11 w-11 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 transition-colors shadow-sm"
            aria-label="Cart"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.75} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[hsl(var(--accent))] text-black text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold border-2 border-[hsl(var(--background))]">
                {count}
              </span>
            )}
          </button>
          {!user && (
            <Link to="/register" className="hidden lg:inline-flex">
              <Button size="sm" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 rounded-sm tracking-[0.1em]">
                {t("nav.register", "Registrati")}
              </Button>
            </Link>
          )}
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="lg:hidden p-3 rounded-sm hover:bg-[hsl(var(--muted))]" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <VisuallyHidden><SheetTitle>Menu</SheetTitle></VisuallyHidden>
              <form onSubmit={doSearch} className="mt-8 mb-2 relative" data-testid="mobile-search">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="mobile-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("nav.search_ph", "Cerca prodotti…")}
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-border rounded-sm bg-card focus:outline-none focus:border-[hsl(var(--primary))]"
                />
              </form>
              <div className="flex flex-col gap-1">
                {items.map((i) => (
                  <Link key={i.to} to={i.to} data-testid={`mobile-nav-${i.to.replace("/", "") || "home"}`} className="py-2.5 text-base font-medium border-b border-border">
                    {i.label}
                  </Link>
                ))}
                <Link to="/b2b" data-testid="mobile-nav-b2b" className="py-2.5 text-base font-semibold text-[hsl(var(--primary))] border-b border-border">B2B Affiliate</Link>
                {!user && (
                  <>
                    <Link to="/login" data-testid="mobile-nav-login" className="py-2.5 text-base">Accedi</Link>
                    <Link to="/register" data-testid="mobile-nav-register" className="py-2.5 text-base font-semibold text-[hsl(var(--primary))]">Registrati</Link>
                  </>
                )}
                {user && (
                  <>
                    {user.is_vendor && <Link to="/azienda" className="py-2.5 text-base font-semibold text-[hsl(var(--primary))]">Backend Azienda</Link>}
                    <Link to={user.is_vendor ? "/azienda" : "/area-utente"} className="py-2.5 text-base">{user.is_vendor ? "La mia azienda" : "Area utente"}</Link>
                    {user.role === "super_admin" && <Link to="/admin" className="py-2.5 text-base text-[hsl(var(--primary))]">Admin</Link>}
                    <button
                      data-testid="mobile-logout-btn"
                      onClick={logout}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 text-base font-semibold rounded-sm border border-border text-foreground hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Esci dall'account
                    </button>
                  </>
                )}
                {/* Social mobile */}
                <div className="mt-6 pt-4 border-t border-border">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Seguici</div>
                  <div className="flex items-center gap-3">
                    <a href="https://www.instagram.com/amazonico3000/" target="_blank" rel="noopener noreferrer" data-testid="mobile-social-instagram" aria-label="Instagram" className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-[hsl(var(--primary))] hover:text-white hover:border-[hsl(var(--primary))]"><Instagram className="h-4 w-4" /></a>
                    <a href="https://www.facebook.com/profile.php?id=61574281947800" target="_blank" rel="noopener noreferrer" data-testid="mobile-social-facebook" aria-label="Facebook" className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-[hsl(var(--primary))] hover:text-white hover:border-[hsl(var(--primary))]"><Facebook className="h-4 w-4" /></a>
                    <a href="https://www.youtube.com/@AMAZONICO3000" target="_blank" rel="noopener noreferrer" data-testid="mobile-social-youtube" aria-label="YouTube" className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-[hsl(var(--primary))] hover:text-white hover:border-[hsl(var(--primary))]"><Youtube className="h-4 w-4" /></a>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* MAIN NAV BAND */}
      <nav className="hidden lg:block border-t border-border/80 bg-[hsl(var(--background))]/80">
        <div className="container-xl h-12 flex items-center justify-center gap-10">
          {items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              data-testid={`nav-${i.to.replace("/", "") || "home"}`}
              end={i.to === "/"}
              className={({ isActive }) =>
                `relative text-[11px] uppercase tracking-[0.32em] font-semibold py-3 transition-colors ${
                  isActive ? "text-[hsl(var(--primary))]" : "text-foreground/70 hover:text-[hsl(var(--primary))]"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative inline-block">
                  {i.label}
                  <span
                    className={`absolute left-1/2 -translate-x-1/2 -bottom-2 h-px bg-[hsl(var(--accent))] transition-all duration-300 ${
                      isActive ? "w-6 opacity-100" : "w-0 opacity-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </header>
  );
}
